import { Injectable } from '@angular/core';
import type { MatDateFormats } from '@angular/material/core';
import { NativeDateAdapter } from '@angular/material/core';

import { DateUtils } from '../shared/utilities/date-utils';

export const APP_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'yyyy-MM-dd',
  },
  display: {
    dateInput: 'yyyy-MM-dd',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'MMMM dd, yyyy',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

type DateDisplayFormat =
  | 'input'
  | 'dateInput'
  | 'monthYearLabel'
  | 'dateA11yLabel'
  | 'monthYearA11yLabel';

@Injectable()
export class AppDateAdapter extends NativeDateAdapter {
  override format(date: Date, displayFormat: unknown): string {
    if (!date || isNaN(date.getTime())) {
      return '';
    }

    const formatKey = displayFormat as DateDisplayFormat;

    switch (formatKey) {
      case 'input':
      case 'dateInput':
        return DateUtils.formatCustom(date, 'yyyy-MM-dd') ?? '';

      case 'monthYearLabel':
        return DateUtils.formatCustom(date, 'MMM yyyy') ?? '';

      case 'dateA11yLabel':
        return DateUtils.formatCustom(date, 'MMMM dd, yyyy') ?? '';

      case 'monthYearA11yLabel':
        return DateUtils.formatCustom(date, 'MMMM yyyy') ?? '';

      default:
        return DateUtils.formatCustom(date, 'yyyy-MM-dd') ?? '';
    }
  }

  override parse(value: unknown): Date | null {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      const parsed = DateUtils.parseDate(value, 'DATE');
      if (parsed) {
        return parsed;
      }

      const iso = DateUtils.fromApi(value);
      if (iso) {
        return iso;
      }

      return null;
    }

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    return null;
  }

  override toIso8601(date: Date): string {
    return DateUtils.toApiString(date) ?? '';
  }
}
